import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { html } from 'lit';
import type { LandingLocale } from '../../shared/content/landingContentTypes.js';
import type { StaticPageContent } from '../../shared/content/staticPageContent.js';
import { staticPageUiCopy } from '../../shared/content/staticPageLocalization.js';
import { editorSemanticText } from '../editor/editorSemanticText.js';
import {
  editorNodeClass,
  editorNodeClick,
  editorNodePointerDown,
  editorNodeStyle,
  type LandingEditorRenderOptions,
} from '../editor/landingEditorRender.js';

export const teamMemberAnchor = (index: number) => `vp-team-member-${index}`;
interface TeamHeroOptions {
  page: StaticPageContent;
  prefix: string;
  sectionStyle: string;
  currentLocale: LandingLocale;
  editor?: LandingEditorRenderOptions;
}
interface FanGesture {
  pointerId: number;
  x: number;
  y: number;
  dragging: boolean;
  suppressClickUntil: number;
}
// Keep the swipe guard across stateful renders without retaining detached stages.
const gestures = new WeakMap<HTMLElement, FanGesture>();
const resetDrag = (stage: HTMLElement, event: PointerEvent) => {
  stage.classList.remove('is-dragging');
  stage.style.removeProperty('--fan-drag');
  if (stage.hasPointerCapture(event.pointerId)) stage.releasePointerCapture(event.pointerId);
};

export const teamHeroSection = stateful(({ page, prefix, sectionStyle, currentLocale, editor }: TeamHeroOptions) => {
  const members = (page.team ?? []).slice(0, 6),
    heroId = `${prefix}.teamHero`;
  const [activeIndex, setActiveIndex] = useState(0);
  const isEditor = editor?.mode === 'editor';
  const selected = isEditor
    ? members.findIndex((_, index) => editor.selectedElementId?.startsWith(`${heroId}.portrait.${index}`))
    : -1;
  const active = selected >= 0 ? selected : activeIndex % Math.max(1, members.length);
  const ui =
    currentLocale === 'tr'
      ? {
          previous: 'Önceki kişi',
          next: 'Sonraki kişi',
          more: (name: string) => `${name} hakkında daha fazla bilgi`,
        }
      : currentLocale === 'en'
        ? {
            previous: 'Previous person',
            next: 'Next person',
            more: (name: string) => `Learn more about ${name}`,
          }
        : {
            previous: 'Vorherige Person',
            next: 'Nächste Person',
            more: (name: string) => `Mehr über ${name} erfahren`,
          };
  const move = (delta: number) => {
    if (members.length < 2) return;
    if (isEditor) editor.onElementSelect?.(heroId);
    setActiveIndex((active + delta + members.length) % members.length);
  };
  const pointerDown = (event: PointerEvent) => {
    if (isEditor || !event.isPrimary || event.button !== 0) return;
    gestures.set(event.currentTarget as HTMLElement, {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      dragging: false,
      suppressClickUntil: 0,
    });
  };
  const pointerMove = (event: PointerEvent) => {
    const stage = event.currentTarget as HTMLElement,
      gesture = gestures.get(stage);
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    const dx = event.clientX - gesture.x,
      dy = event.clientY - gesture.y;
    if (!gesture.dragging && Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) {
      gestures.delete(stage);
      return;
    }
    if (!gesture.dragging && Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy) * 1.2) {
      gesture.dragging = true;
      stage.setPointerCapture(event.pointerId);
      stage.classList.add('is-dragging');
    }
    if (gesture.dragging) {
      event.preventDefault();
      stage.style.setProperty('--fan-drag', `${Math.max(-90, Math.min(90, dx * 0.65))}px`);
    }
  };
  const pointerUp = (event: PointerEvent) => {
    const stage = event.currentTarget as HTMLElement,
      gesture = gestures.get(stage);
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    if (gesture.dragging) {
      gesture.suppressClickUntil = performance.now() + 450;
      if (Math.abs(event.clientX - gesture.x) > 36) move(event.clientX < gesture.x ? 1 : -1);
    }
    gesture.pointerId = -1;
    resetDrag(stage, event);
  };
  const pointerCancel = (event: PointerEvent) => {
    const stage = event.currentTarget as HTMLElement;
    gestures.delete(stage);
    resetDrag(stage, event);
  };
  const visit = (event: MouseEvent, index: number) => {
    const id = `${heroId}.portrait.${index}`;
    if (isEditor) {
      editorNodeClick(editor, id, event);
      return;
    }
    const stage = (event.currentTarget as HTMLElement).closest<HTMLElement>('.vp-team-hero-stage');
    if (stage && (gestures.get(stage)?.suppressClickUntil ?? 0) > performance.now()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    const card = document.getElementById(teamMemberAnchor(index));
    if (!card) return;
    event.preventDefault();
    event.stopPropagation();
    const navigationHeight = document.querySelector('.vp-navigation')?.getBoundingClientRect().height ?? 112;
    // The page already reserves space for the fixed navigation via scroll-padding.
    const scrollContainer = card.closest('.vp-page');
    const scrollPadding = scrollContainer
      ? Number.parseFloat(getComputedStyle(scrollContainer).scrollPaddingTop) || 0
      : 0;
    card.style.scrollMarginTop = `${Math.max(20, navigationHeight + 20 - scrollPadding)}px`;
    card.focus({ preventScroll: true });
    card.scrollIntoView({
      block: 'start',
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    });
    history.replaceState(history.state, '', `#${teamMemberAnchor(index)}`);
  };
  return html`<section
    class=${editorNodeClass(editor, heroId, 'vp-static-hero vp-team-hero vp-team-hero--people')}
    style=${sectionStyle}
    @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, heroId, event)}
    @click=${(event: MouseEvent) => editorNodeClick(editor, heroId, event)}
  >
    <div class="vp-team-hero-word" aria-hidden="true">Team</div>
    <div class="vp-team-hero-lines" aria-hidden="true"><span></span><span></span><span></span></div>
    <div class="vp-shell vp-team-hero-layout">
      ${editorSemanticText({
        editor,
        elementId: `${heroId}.title`,
        fallbackTag: 'h1',
        className: 'vp-team-hero-title vp-visually-hidden',
        content: page.title,
      })}
      <div
        class="vp-team-hero-stage"
        aria-label=${staticPageUiCopy(currentLocale).teamPreview}
        data-active-index=${active}
        @pointerdown=${pointerDown}
        @pointermove=${pointerMove}
        @pointerup=${pointerUp}
        @pointercancel=${pointerCancel}
        @keydown=${(event: KeyboardEvent) => {
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            event.stopPropagation();
            move(event.key === 'ArrowRight' ? 1 : -1);
          }
        }}
      >
        ${members.map((member, index) => {
          const portraitId = `${heroId}.portrait.${index}`;
          let distance = (index - active + members.length) % members.length;
          if (distance > members.length / 2) distance -= members.length;
          return html`<figure
            data-member-index=${index}
            class=${editorNodeClass(
              editor,
              portraitId,
              `vp-team-hero-portrait vp-team-hero-portrait-${index}${index === active ? ' is-active' : ''}`,
            )}
            style=${`--fan-distance:${distance};--fan-depth:${Math.abs(distance)};--fan-layer:${members.length - Math.abs(distance)};${editorNodeStyle(editor, portraitId)}`}
            @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, portraitId, event)}
            @click=${(event: MouseEvent) => editorNodeClick(editor, portraitId, event)}
          >
            <a
              class="vp-team-fan-link"
              href=${`#${teamMemberAnchor(index)}`}
              aria-label=${ui.more(member.name)}
              @click=${(event: MouseEvent) => visit(event, index)}
              @focus=${(event: FocusEvent) => {
                if (!isEditor && (event.currentTarget as HTMLElement).matches(':focus-visible')) setActiveIndex(index);
              }}
            >
              <img
                class=${editorNodeClass(editor, `${portraitId}.image`)}
                style=${editorNodeStyle(editor, `${portraitId}.image`)}
                src=${member.image}
                alt=${member.name}
                draggable="false"
                @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${portraitId}.image`, event)}
                @click=${(event: MouseEvent) => editorNodeClick(editor, `${portraitId}.image`, event)}
              />
            </a>
          </figure>`;
        })}
      </div>
      <div class="vp-team-fan-controls" @pointerdown=${(event: PointerEvent) => event.stopPropagation()}>
        <button
          type="button"
          aria-label=${ui.previous}
          ?disabled=${members.length < 2}
          @click=${(event: MouseEvent) => {
            event.stopPropagation();
            move(-1);
          }}
        >
          <span class="icon-[material-symbols--arrow-back-rounded]" aria-hidden="true"></span>
        </button>
        <span class="vp-visually-hidden" aria-live="polite" aria-atomic="true">${members[active]?.name}</span>
        <button
          type="button"
          aria-label=${ui.next}
          ?disabled=${members.length < 2}
          @click=${(event: MouseEvent) => {
            event.stopPropagation();
            move(1);
          }}
        >
          <span class="icon-[material-symbols--arrow-forward-rounded]" aria-hidden="true"></span>
        </button>
      </div>
    </div>
  </section>`;
});
